import PocketBase, { type AdminModel, type RecordModel } from "pocketbase";
import { apiUrl } from "$lib/api";
import { get, writable } from "svelte/store";
import { checkRole, ROLES } from "$lib/rolePermissions";

export const isAdmin = writable(false);
export const isDeveloper = writable(false);
export const isUser = writable(false);

let roleCheckInFlight: Promise<void> | null = null;

if (typeof window !== "undefined") {
  const storedAdmin = sessionStorage.getItem("isAdmin");
  const storedDev = sessionStorage.getItem("isDeveloper");
  const storedUser = sessionStorage.getItem("isUser");

  if (storedAdmin !== null) {
    isAdmin.set(storedAdmin === "true");
  }
  if (storedDev !== null) {
    isDeveloper.set(storedDev === "true");
  }
  if (storedUser !== null) {
    isUser.set(storedUser === "true");
  }
}

export function saveToSessionStore() {
  sessionStorage.setItem("isAdmin", get(isAdmin).toString());
  sessionStorage.setItem("isDeveloper", get(isDeveloper).toString());
  sessionStorage.setItem("isUser", get(isUser).toString());
}
export async function checkAndUpdateRole() {
  if (!get(currentUser)) return;

  if (roleCheckInFlight) return roleCheckInFlight;

  roleCheckInFlight = (async () => {
    const user = get(currentUser);
    const isAdminValue = await checkRole(user, ROLES.ADMIN);
    const isDeveloperValue = await checkRole(user, ROLES.DEVELOPER);
    const isUserValue = await checkRole(user, ROLES.USER);
    isAdmin.set(isAdminValue);
    isDeveloper.set(isDeveloperValue);
    isUser.set(isUserValue);
    saveToSessionStore();
  })();

  try {
    await roleCheckInFlight;
  } finally {
    roleCheckInFlight = null;
  }
}
export function resetRoles() {
  isAdmin.set(false);
  isDeveloper.set(false);
  isUser.set(false);
  sessionStorage.removeItem("isAdmin");
  sessionStorage.removeItem("isDeveloper");
  sessionStorage.removeItem("isUser");
}
isAdmin.subscribe(($isAdmin) => {
  sessionStorage.setItem("isAdmin", JSON.stringify($isAdmin));
});

isDeveloper.subscribe(($isDeveloper) => {
  sessionStorage.setItem("isDeveloper", JSON.stringify($isDeveloper));
});

isUser.subscribe(($isUser) => {
  sessionStorage.setItem("isUser", JSON.stringify($isUser));
});

export function createInstance() {
  let pb = new PocketBase(apiUrl);
  return pb;
}

export const pb = createInstance();

export type AuthUser = AdminModel | RecordModel;
export const currentUser = writable<AuthUser | null>(null);

function validateArea(data: any) {
  if (
    typeof data.name !== "string" ||
    typeof data.x !== "number" ||
    typeof data.y !== "number" ||
    typeof data.x2 !== "number" ||
    typeof data.y2 !== "number" ||
    (data.name !== "reference" && data.name !== "candidate") ||
    data.x >= data.x2 ||
    data.y >= data.y2
  ) {
    throw new Error("Ungültige Daten für Ignorierbereich");
  }
}

export async function saveArea(area: any) {
  try {
    const data = {
      name: area.name,
      type: area.type,
      x: Math.max(1, Number(area.x)),
      y: Math.max(1, Number(area.y)),
      x2: Math.max(area.x + 1, Number(area.x + area.width)),
      y2: Math.max(area.y + 1, Number(area.y + area.height))
    };
    const record = await pb.collection("areas").create(data);
    record.width = record.x2 - record.x;
    record.height = record.y2 - record.y;
    return record;
  } catch (error) {
    throw error;
  }
}

export async function getAreas() {
  try {
    const records = await pb.collection("areas").getFullList();
    return records.map((record) => ({
      ...record,
      width: Number(record.x2) - Number(record.x),
      height: Number(record.y2) - Number(record.y)
    }));
  } catch (error) {
    throw error;
  }
}

export async function deleteArea(id: any) {
  try {
    await pb.collection("areas").delete(id);
  } catch (error) {
    throw error;
  }
}

export async function saveLayoutGroup(group: any) {
  try {
    const record = await pb.collection("layout_groups").create(group);
    return record;
  } catch (error) {
    console.error("Fehler beim Speichern der Layout-Gruppe:", error);
    throw error;
  }
}

export async function getLayoutGroups() {
  try {
    const records = await pb.collection("layout_groups").getFullList();
    return records;
  } catch (error) {
    console.error("Fehler beim Abrufen der Layout-Gruppen:", error);
    throw error;
  }
}

export async function updateLayoutGroup(id: any, data: any) {
  try {
    const record = await pb.collection("layout_groups").update(id, data);
    return record;
  } catch (error) {
    console.error("Fehler beim Aktualisieren der Layout-Gruppe:", error);
    throw error;
  }
}

export async function deleteLayoutGroup(id: any) {
  try {
    await pb.collection("layout_groups").delete(id);
  } catch (error) {
    console.error("Fehler beim Löschen der Layout-Gruppe:", error);
    throw error;
  }
}
